import { Box, UseDisclosureReturn } from "@chakra-ui/react"
import { motion } from "framer-motion"
import { useEffect } from "react"
import {
  Redirect,
  Route,
  Router,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom"
import {
  AugmentationToolbarChildren,
  AugmentationToolbarHookReturn,
  AugmentationToolbarOption
} from "./AugmentationToolbar"
import useQueryParamHistory, {
  useQueryParamState
} from "./useQueryParamHistory"

type AugmentationToolbarRoutingProps = {
  toolbar: AugmentationToolbarHookReturn
  // For rendering a constant element regardless of current route.
  renderConstant?: () => React.ReactNode
  // For rendering the outlet for the current route.
  renderOutlet?: (outlet: React.ReactNode) => React.ReactNode
  queryParamKey: string
}

type OnEnterProps = {
  toolbar: AugmentationToolbarHookReturn
  option: AugmentationToolbarOption
}

// When a route is entered, select the root toolbar option.
function OnEnter({ toolbar, option }: OnEnterProps) {
  useEffect(() => {
    toolbar.onSelect(option.compositeKey || option.key)
  }, [])

  return null
}

// When no route matches, we call the on404 callback
// to let the app router handle the missing route.
function MissingRoute({ on404 }: { on404: (pathname: string) => void }) {
  const location = useLocation()

  useEffect(() => {
    // Augmentation routing doesn't use an ending slash.
    if (location.pathname !== "/" && !location.pathname.endsWith("/"))
      // Avoid calling on404 for the root path
      on404(location.pathname + location.search)
  }, [location, on404])

  // Found route with closing slash, redirect it to the same path without the slash.
  if (location.pathname.endsWith("/")) {
    return (
      <Redirect
        to={{
          ...location,
          pathname: location.pathname.slice(0, -1)
        }}
      />
    )
  }

  return null
}

// Handle opening and closing the augmentation based on presence query param.
export function useAugmentationQueryParamDisclosure({
  disclosure,
  queryParamKey
}: {
  disclosure: UseDisclosureReturn
  queryParamKey: string
}) {
  const { queryParamPath } = useQueryParamState(queryParamKey)

  useEffect(() => {
    if (queryParamPath) {
      if (!disclosure.isOpen) disclosure.onOpen()
    } else {
      if (disclosure.isOpen) disclosure.onClose()
    }
  }, [queryParamPath])
}

function AugmentationToolbarRouting({
  renderOutlet,
  renderConstant,
  toolbar,
  queryParamKey
}: AugmentationToolbarRoutingProps) {
  renderOutlet ||= (outlet: React.ReactNode) => outlet
  // Use the app route history.
  const history = useHistory()
  const queryParamHistory = useQueryParamHistory(queryParamKey)
  const { setQueryParamPath } = useQueryParamState(queryParamKey)

  // Set the query param when a base toolbar option becomes selected.
  useEffect(() => {
    if (toolbar.stack && toolbar.stack.length === 1) {
      setQueryParamPath(`/${toolbar.stack[0].key}`)
    }
  }, [toolbar.stack])

  // Remove the query param when the component unmounts.
  useEffect(() => {
    return setQueryParamPath
  }, [])

  // Flatten all routes into a single array.
  // This allows us to sort them by specificity and render them in order.
  // A flattened structure also means only 1 route is rendered at a time (
  // we don't need nested routes).
  function flattenRoutes(
    children: AugmentationToolbarChildren,
    basePath = ""
  ): Array<{ path: string; option: any }> {
    const routes: Array<{ path: string; option: any }> = []

    children.forEach((option) => {
      if (option.AugmentationComponent) {
        const path = basePath
          ? `${basePath}/${option.key}`
          : `/:optionKey(${option.key})`
        routes.push({ path, option })

        // Add nested routes
        if (option.children) {
          routes.push(...flattenRoutes(option.children, path))
        }
      }
    })

    return routes
  }

  const allRoutes = flattenRoutes(toolbar.options)

  // Sort routes by specificity (most specific first)
  const sortedRoutes = allRoutes.sort((a, b) => {
    return b.path.split("/").length - a.path.split("/").length
  })

  return (
    <Router history={queryParamHistory}>
      <Route path="*">{renderConstant?.()}</Route>
      <Switch>
        {sortedRoutes.map(({ path, option }) => (
          <Route
            key={path}
            path={path}
            exact
            strict
            children={({ match }) => (
              <>
                <OnEnter toolbar={toolbar} option={option} />
                {renderOutlet!(
                  <Box
                    as={motion.div}
                    initial={{ opacity: 0, y: -30 }}
                    animate={{
                      opacity: 1,
                      y: 0
                    }}
                    w="100%"
                    minH="100%"
                    display="flex"
                  >
                    <option.AugmentationComponent
                      toolbar={toolbar}
                      match={match}
                    />
                  </Box>
                )}
              </>
            )}
          />
        ))}
        <Route path="*">
          <MissingRoute on404={history.replace} />
        </Route>
      </Switch>
    </Router>
  )
}

export default AugmentationToolbarRouting
